import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

// Components
import { useSelector } from 'react-redux';

import { createLabel } from 'common/components/InsuranceSelect/InsuranceSelect';
import { otherInsurance, topCanadianInsurance } from 'common/constants';
import {
  Div,
  InputLabel,
  StyledFormItem,
} from 'common/styles/oc-styled-components';

// Other
import { Autocomplete } from 'pages/common/components/Autocomplete/Autocomplete';
import { DIRECTION } from 'pages/common/components/Autocomplete/types';

import { selectDataInsuranceListForCanada } from '../../../../connect/selectors';

const InsurancePickerCanada = ({
  defaultProvider,
  preferredDropDirection,
  form,
  updateInsurance,
  checkRequiredFields = () => {},
  hidePopular = false,
  showNotListed = false,
}) => {
  const insuranceList = useSelector(selectDataInsuranceListForCanada);

  const [selectedProvider, setSelectedProvider] = useState(defaultProvider);

  const topProviders = useMemo(() => {
    if (!insuranceList?.length) {
      return [];
    }

    return insuranceList.filter(({ name }) => topCanadianInsurance.includes(name));
  }, [insuranceList]);

  const otherProviders = useMemo(() => {
    if (!insuranceList?.length) {
      return [];
    }

    return insuranceList.filter(({ name }) => !topCanadianInsurance.includes(name));
  }, [insuranceList]);

  const providerList = useMemo(() => {
    const combinedList = [
      createLabel('All Providers'),
      ...otherProviders,
    ];

    if (!hidePopular) {
      combinedList.unshift(
        createLabel('Popular Providers'),
        ...topProviders,
      );
    }

    if (showNotListed) {
      combinedList.unshift(
        createLabel("Don't see your provider?"),
        otherInsurance,
      );
    }

    return combinedList;
  },[hidePopular, otherProviders, showNotListed, topProviders]);

  const selectNewInsuranceProvider = providerId => {
    setSelectedProvider(providerId);

    const provider = providerList.find(item => item.id === providerId);
    updateInsurance(provider);
    if (form) {
      form.setFieldsValue({
        insuranceCarrierCanada: provider?.name,
      });
    }

    checkRequiredFields();
  };

  return (
    <StyledFormItem
      name="insuranceCarrierCanada"
      initialValue={selectedProvider}
      rules={[
        {
          required: true,
          message:
            'Required. Check your insurance membership card or employer website.',
        },
      ]}
      validateTrigger="onSubmit"
    >
      <Div data-testid="insurancePickerCanada" margin="0" position="relative">
        <Autocomplete
          preferredDropDirection={preferredDropDirection}
          onSelect={selectNewInsuranceProvider}
          defaultSelected={selectedProvider}
          options={providerList}
          shouldAutoFocus
        />
        <InputLabel>Insurance Provider</InputLabel>
      </Div>
    </StyledFormItem>
  );
};

InsurancePickerCanada.propTypes = {
  defaultProvider: PropTypes.string,
  preferredDropDirection: PropTypes.oneOf(Object.values(DIRECTION)),
  form: PropTypes.object,
  updateInsurance: PropTypes.func.isRequired,
  checkRequiredFields: PropTypes.func,
  hidePopular: PropTypes.bool,
  showNotListed: PropTypes.bool,
};

export default InsurancePickerCanada;
